/**
 * Legenda ao vivo sobre o vídeo (esboço 3.3): o nome de quem falou em ciano e a fala, que some alguns segundos
 * depois. Guia-se pelo instante em que a fala CHEGOU a este navegador (`liveSegment.receivedAt`); a lista
 * inicial (histórico) nunca vira legenda. Começa desligada em todas as janelas (a transcrição já está no painel):
 * a pessoa liga pelo menu "Mais opções" e a escolha vale daí em diante (`captionsPref`).
 */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useCallTranscript } from './transcriptContext';

/** Por quanto tempo a fala fica na tela depois de chegar. */
export const CAPTION_MS = 7000;
const MAX_CHARS = 220;

/** A legenda está ligada? Só se a pessoa ligou. */
export const captionsEnabled = (pref) => pref === true;

/** Fala longa: mostra o fim (o que está sendo dito agora), cortando numa palavra inteira. */
export function clipCaption(text, max = MAX_CHARS) {
    if (text.length <= max) return text;
    const cut = text.length - max;
    const tail = text.slice(cut);
    if (text[cut - 1] === ' ') return `…${tail}`; // o corte caiu no começo de uma palavra
    const space = tail.indexOf(' ');
    return `…${space >= 0 && space < 40 ? tail.slice(space + 1) : tail}`;
}

function useNow(active) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!active) return undefined;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [active]);
    return now;
}

/** @param {{ name: string, text: string, size: 'room'|'compact' }} props */
export function LiveCaptionsView({ name, text, size }) {
    return (
        <div
            role="status"
            aria-live="off"
            data-live-captions=""
            className={cn(
                // z-20: acima da prévia do atendente (z-10, que ele arrasta): a legenda some em segundos, a prévia fica.
                'pointer-events-none absolute left-3 z-20 max-w-[calc(100%-1.5rem)] rounded-xl bg-black/75 text-white shadow-lg',
                size === 'room' ? 'bottom-14 px-3 py-2 min-[1100px]:max-w-[min(70%,640px)] min-[1100px]:px-4 min-[1100px]:py-2.5' : 'bottom-11 px-3 py-2',
            )}
        >
            <p className={cn('font-semibold text-cyan-300', size === 'room' ? 'text-sm' : 'text-xs')}>{name}</p>
            <p className={cn('leading-snug', size === 'room' ? 'text-base min-[1100px]:text-lg' : 'text-sm')}>{clipCaption(text)}</p>
        </div>
    );
}

/** @param {{ variant: 'compact'|'room'|'guest' }} props */
export function LiveCaptions({ variant }) {
    const transcript = useCallTranscript();
    const on = Boolean(transcript) && captionsEnabled(transcript.captionsPref, variant);
    const live = transcript?.liveSegment ?? null;
    const now = useNow(on && live !== null);
    if (!on || !live || now - live.receivedAt > CAPTION_MS) return null;
    const { segment } = live;
    return (
        <LiveCaptionsView
            name={segment.identity === transcript.localIdentity ? 'Você' : segment.name}
            text={segment.text}
            size={variant === 'room' ? 'room' : 'compact'}
        />
    );
}
